// CostLogForm constants
//
// COST CODES — O&G drilling chart-of-accounts (DRAFT pending PO ratification; see
// docs/DRILLING_COST_CODE_BOOK.md). Replaces the geothermal CSI 33-64xx vestige.
// code1 = 4-digit account (10AA); code2 = 6-digit line item (10AALL). Each account
// is Intangible (IDC) or Tangible (TDC) per COPAS-AFE convention — see
// COST_CODE_TAX_DEFAULTS. This is the shared source the registry seed derives from,
// so both the cost log and the AFE builder resolve these codes.

export const COST_LOG_CODE1_OPTIONS = [
  "1001", // Location, Mobilization & Permits
  "1002", // Drilling
  "1003", // Well Control, Fishing & Trouble
  "1005", // Evaluation & Logging
  "1006", // Completion (services)
  "1007", // Support & Indirect
  "1009", // Contingency
  "1011", // Surface Facilities & Equipment (tangible)
  "1013", // Plug & Abandonment (services)
  "1014", // Casing & Tubulars (tangible)
  "1015", // Wellhead & Tree (tangible)
  "1016", // Downhole & Artificial Lift Equipment (tangible)
  "1099", // Other / Uncoded
] as const;

export type CostLogCode1 = (typeof COST_LOG_CODE1_OPTIONS)[number];

// O&G cost-coding dimensions. DRAFT defaults — product owner to refine the
// lists for the geothermal/O&G workflow. Picklists keep the data queryable
// (per the data-model rule: structured over free text).

/** Hole / casing interval the cost is attributed to. */
export const COST_INTERVAL_OPTIONS = [
  "Surface",
  "Conductor",
  "Intermediate",
  "Production",
  "Liner",
  "Open Hole",
  "Riser",
  "N/A",
] as const;
export type CostInterval = (typeof COST_INTERVAL_OPTIONS)[number];

/** Project / well-construction phase. */
export const COST_PHASE_OPTIONS = [
  "Mobilization",
  "Site Prep",
  "Drilling",
  "Casing & Cementing",
  "Completion",
  "Pressure Testing",
  "Logging & Survey",
  "Workover",
  "Plug & Abandonment",
  "Demobilization",
] as const;
export type CostPhase = (typeof COST_PHASE_OPTIONS)[number];

/** Operation / activity the cost was incurred during. */
export const COST_ACTIVITY_OPTIONS = [
  "Drilling",
  "Tripping",
  "Casing",
  "Cementing",
  "Circulating",
  "Logging",
  "Coring",
  "Testing",
  "Fishing",
  "Rig Repair",
  "Waiting (NPT)",
  "Rig Up / Down",
  "Other",
] as const;
export type CostActivity = (typeof COST_ACTIVITY_OPTIONS)[number];

/** Human-readable labels for the code1 accounts. */
export const COST_CODE_LABELS: Record<CostLogCode1, string> = {
  "1001": "Location, Mobilization & Permits",
  "1002": "Drilling",
  "1003": "Well Control, Fishing & Trouble",
  "1005": "Evaluation & Logging",
  "1006": "Completion",
  "1007": "Support & Indirect",
  "1009": "Contingency",
  "1011": "Surface Facilities & Equipment",
  "1013": "Plug & Abandonment",
  "1014": "Casing & Tubulars",
  "1015": "Wellhead & Tree",
  "1016": "Downhole & Artificial Lift Equipment",
  "1099": "Other / Uncoded",
};

/** code2 subcodes per code1 account — the dependent line-item picklist (WellView
 *  code1→code2 model). DRAFT (docs/DRILLING_COST_CODE_BOOK.md). value = 6-digit
 *  line code (10AALL); label = the line item. Off-list free entry stays possible
 *  for an imported/legacy line the picklist doesn't cover. */
export const COST_CODE2_SUBCODES: Record<
  CostLogCode1,
  readonly { value: string; label: string }[]
> = {
  "1001": [
    { value: "100101", label: "Rig mobilization & demobilization" },
    { value: "100102", label: "Rig & camp mobilization to pad" },
    { value: "100110", label: "Location build & pad construction" },
    { value: "100111", label: "Access road & site grading" },
    { value: "100112", label: "Reserve pit / containment & liner" },
    { value: "100130", label: "Permitting, survey & regulatory" },
  ],
  "1002": [
    { value: "100201", label: "Drilling rig dayrate & crew" },
    { value: "100202", label: "Drilling fluids / mud & chemicals" },
    { value: "100203", label: "Bits, motors & MWD/LWD (directional)" },
    { value: "100204", label: "Cementing services (pumping)" },
    { value: "100205", label: "Drilling rentals (tools, tanks, equipment)" },
    { value: "100206", label: "Fuel & power" },
    { value: "100207", label: "Rig supervision & company reps" },
    { value: "100208", label: "Casing crews & running services" },
    { value: "100209", label: "Mud logging" },
    { value: "100210", label: "Solids control & drilling-waste" },
    { value: "100211", label: "Cement materials (cement, additives, spacer)" },
    { value: "100212", label: "Water sourcing, disposal (SWD) & hauling" },
  ],
  "1003": [
    { value: "100301", label: "Well-control services" },
    { value: "100302", label: "Fishing tools & services" },
    { value: "100303", label: "Lost circulation & remediation" },
    { value: "100304", label: "BOP & pressure-control rental" },
  ],
  "1005": [
    { value: "100501", label: "Open-hole wireline logging" },
    { value: "100502", label: "Cased-hole logging" },
    { value: "100503", label: "Coring & core analysis" },
    { value: "100504", label: "Formation testing / DST" },
    { value: "100505", label: "Definitive / gyro survey" },
  ],
  "1006": [
    { value: "100601", label: "Completion fluids & wellhead install" },
    { value: "100602", label: "Stimulation / frac services" },
    { value: "100603", label: "Perforating" },
    { value: "100604", label: "Sand control / gravel-pack" },
    { value: "100605", label: "Coiled tubing / snubbing" },
    { value: "100606", label: "Completion rig / workover unit" },
    { value: "100607", label: "Well testing & flowback" },
    { value: "100608", label: "Proppant & frac chemicals" },
  ],
  "1007": [
    { value: "100701", label: "Supervision, trucking & support" },
    { value: "100702", label: "Roustabout & labor" },
    { value: "100703", label: "Communications, safety & environmental" },
    { value: "100704", label: "Overhead & administration" },
  ],
  "1009": [{ value: "100901", label: "Contingency / trouble-cost reserve" }],
  "1011": [
    { value: "101110", label: "Surface facilities & flowlines" },
    { value: "101111", label: "Tanks, separators & vessels" },
  ],
  "1013": [
    { value: "101301", label: "P&A rig & services" },
    { value: "101302", label: "Cement plugs & wellhead removal" },
  ],
  "1014": [
    { value: "101401", label: "Conductor casing & accessories" },
    { value: "101402", label: "Surface casing & accessories" },
    { value: "101403", label: "Intermediate casing & accessories" },
    { value: "101404", label: "Production casing & accessories" },
    { value: "101405", label: "Liner & liner hanger" },
    { value: "101406", label: "Tubing & accessories" },
    { value: "101407", label: "Torque/turn services" },
    { value: "101408", label: "Casing running tool" },
  ],
  "1015": [
    { value: "101501", label: "Wellhead & casing head" },
    { value: "101502", label: "Christmas tree & valves" },
  ],
  "1016": [
    { value: "101601", label: "Packers & permanent downhole equipment" },
    { value: "101602", label: "Pump / ESP / rod / PCP equipment" },
    { value: "101603", label: "Screens & sand-control hardware" },
  ],
  "1099": [{ value: "109901", label: "Miscellaneous (uncoded)" }],
};

// ---------------------------------------------------------------------------
// Parity fields — option sets (OpenWells dm_dailycost) + US IDC/TDC defaults
// ---------------------------------------------------------------------------

/** Units of measure for a cost line's quantity. DRAFT — PO/accounting to refine. Labels only (the
 *  picklist the user selects); cost quantities are not unit-converted. Mixed imperial/SI + count/
 *  time units, as O&G cost tickets use. Free entry is also allowed (this list is a convenience). */
export const COST_UOM_OPTIONS = [
  "each",
  "day",
  "hr",
  "joint",
  "ft",
  "m",
  "bbl",
  "gal",
  "m3",
  "lb",
  "kg",
  "sk",
  "ton",
  "tonne",
  "lot",
  "mile",
  "km",
  "gpm",
  "klbs",
] as const;
export type CostUom = (typeof COST_UOM_OPTIONS)[number];

/** US tax classification options (see CostTaxClass in types.ts). */
export const COST_TAX_CLASS_OPTIONS = [
  { value: "IDC", label: "IDC — Intangible" },
  { value: "TDC", label: "TDC — Tangible" },
] as const;

/** DRAFT default IDC/TDC by cost code — a CONVENIENCE seed to pre-fill the classification when a
 *  code is selected; ALWAYS overridable per line. Tax classification is accounting/jurisdiction-
 *  critical: this map is NOT authoritative and MUST be ratified by accounting (Charter Abs-5 —
 *  money-critical). Only clearly one-or-the-other codes are seeded; everything else stays "" so the
 *  user classifies. Codes absent here → no default. */
export const COST_CODE_TAX_DEFAULTS: Partial<
  Record<CostLogCode1, "IDC" | "TDC">
> = {
  // Intangible (services / labor / time / consumables) — non-salvageable.
  "1001": "IDC", // location, mobilization & permits
  "1002": "IDC", // drilling (dayrate, fluids, services)
  "1003": "IDC", // well control, fishing & trouble
  "1005": "IDC", // evaluation & logging
  "1006": "IDC", // completion (services)
  "1007": "IDC", // support & indirect
  "1009": "IDC", // contingency
  "1013": "IDC", // plug & abandonment (services)
  // "1099" Other / Uncoded — deliberately NO default (it has "no opinion"; the
  // user classifies, and an existing classification is preserved when re-coded to it).
  // Tangible (salvageable equipment / capitalized material).
  "1011": "TDC", // surface facilities & equipment
  "1014": "TDC", // casing & tubulars
  "1015": "TDC", // wellhead & tree
  "1016": "TDC", // downhole & artificial-lift equipment
};
